import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import type { PaginatedActivityLogResponse } from "../types/activity";

interface ApiResponse {
  success: boolean;
  data: PaginatedActivityLogResponse;
}

const PAGE_LIMIT = 20;

export function useActivityLogs(candidateStatusId: string | undefined) {
  const query = useInfiniteQuery({
    queryKey: ["activity-logs", candidateStatusId] as const,
    queryFn: ({ signal, pageParam }) =>
      apiClient.get<ApiResponse>(
        `/candidate-statuses/${candidateStatusId}/activity-logs?page=${pageParam}&limit=${PAGE_LIMIT}`,
        { signal }
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { total, page, limit } = lastPage.data;
      return page * limit < total ? page + 1 : undefined;
    },
    enabled: !!candidateStatusId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const items = query.data?.pages.flatMap((page) => page.data.items) ?? [];

  return {
    items,
    total: query.data?.pages[0]?.data.total ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error ? "Failed to load activity logs." : null,
    refetch: query.refetch,
  };
}
